import { Injectable } from "@angular/core";
import { LineStatus, MediaType, OrderType, ErrorType, DrmType, OrderSourceSystem } from "../../models/enums";
import { OrderShipment, ErrorMessage, OrderFilter, OrderShipmentGroup, OrderLine } from "../../models";
import { orderBy } from "lodash-es";
import { Market } from "src/app/models/market";
import { LegendItem } from "../../modules/operational-dashboard/models/legend-item";
import { OrderShipmentGroupsList } from "../../models/order-shipment-groups-list";

@Injectable({
  providedIn: "root",
})
export class OrderUtilityService {
  private static readonly MARKET_NAMES: Record<string, string> = {
    ALL: "All Markets",
    AMFS: "Art Museums and Film Societies",
    AUDC: "Audio Cine",
    CC: "Residence Life",
    CGRV: "Campgrounds/RV Parks",
    CHUR: "Churches",
    CO: "College Sales",
    COL: "College Sales",
    CX: "Cruiseship",
    DAYC: "Daycare Centers",
    DC: "Digital Campus",
    DCKS: "Digital Campus K-12",
    DIVP: "Non-Traditional Diversified Program",
    ENT: "Entertainment",
    FBAR: "France - Bar & Pub",
    FCH: "France - Church & Religious Org",
    FCIN: "France - Cinematheque",
    FCLB: "France - Club (Leisure & Sport)",
    FCMP: "France - Campsite/Holiday Resort",
    FCSR: "France - Customer Screening",
    FEAP: "France - Event Agency & Planner",
    FERY: "Ferries",
    FFLC: "France - Filmclub",
    FFST: "France - Festival & Fair",
    FGCC: "France - Community of Cities",
    FGCH: "France - City Hall",
    FGEC: "France - Embassy & Consulate",
    FGHA: "France - Government Help Agency",
    FGSA: "France - Social Ctr/Animation Com",
    FGTO: "France - Tourist Office",
    FHOT: "France - Hotel & B&B",
    FHSP: "France - Hospital & Clinique",
    FLIB: "France - Library",
    FLMB: "Filmbank",
    FLPA: "France - Leisure Park & Activity",
    FMIL: "France - Military",
    FMTH: "France - Movie Theater",
    FMUS: "France - Museum",
    FOIL: "France - Oil Rig",
    FPRI: "France - Prison",
    FRH: "France - Retirement & Conval Home",
    FRST: "France - Restaurant",
    FSCH: "France - School",
    FSFF: "Film Societies and Film Festivals",
    FSMA: "France - Social & Medial Assoc",
    FSSC: "France - Secondary School",
    FSSR: "France - Staff Screening",
    FTAP: "France - Airport & Common Lounge",
    FTFY: "France - Ferry",
    FTHO: "France - Theater & Opera",
    FTL: "France - Teaching Lounge",
    FTMO: "France - Motorcoach",
    FTTR: "France - Train",
    FUNV: "France - University",
    GENT: "Swank France - General Entertainment",
    GOVT: "Government",
    HOSP: "Hospitals",
    HOTL: "Hotel - Filmbank",
    ILRS: "Inns/Lodges/Resorts",
    INSD: "Correctional Institutions Dig.",
    INST: "Correctional Institutions",
    INVL: "Institutions Video Library",
    ITD: "IT&D",
    LIB: "Public Libraries Division",
    MCD: "Motorcoach Dig. Movies",
    MCM: "Motorcoach Movies",
    MRTD: "Inmarsat - Domestic",
    MRTM: "Filmbank - Maritime",
    OBM: "On Board Movies",
    OILR: "Oil Rigs",
    PACC: "Performing Arts, Cultural Centers",
    PARK: "Parks & Rec",
    PRDC: "Producer",
    PRKS: "Filmbank - Parks",
    RLC: "Residence Life Cinema",
    RSRT: "Resorts & Hotels",
    SALE: "Sales Rep.",
    THET: "Theater",
  };

  public mapOrderShipmentStatus(status: LineStatus | number): string {
    switch (status) {
      case LineStatus.Open:
        return "Open";
      case LineStatus.Pending:
        return "Pending";
      case LineStatus.Watermarking:
        return "Watermarking";
      case LineStatus.Transcoding:
        return "Transcoding";
      case LineStatus.Encrypting:
        return "Encrypting";
      case LineStatus.Packaging:
        return "Packaging";
      case LineStatus.Authoring:
        return "Authoring";
      case LineStatus.PinPlay:
        return "PinPlay";
      case LineStatus.Licensing:
        return "Licensing";
      case LineStatus.Uploading:
        return "Uploading";
      case LineStatus.Burning:
        return "Burning";
      case LineStatus.IngestingMetadata:
        return "Ingesting Metadata";
      case LineStatus.Complete:
        return "Complete";
      case LineStatus.Cancelled:
        return "Cancelled";
      case LineStatus.PendingDownload:
        return "Pending Download";
      case LineStatus.Shipped:
        return "Shipped";
      case LineStatus.PendingShipped:
        return "Pending Shipped";
      case LineStatus.PostedOrClosed:
        return "Posted or Closed";
      default:
        return "Unknown";
    }
  }

  public mapOrderOrderType(orderType: OrderType): string {
    switch (orderType) {
      case OrderType.Adebs:
        return "ADEBS";
      case OrderType.Cinelink:
        return "CineLink";
      case OrderType.MyHotelMovies: // My Hotel Movies is an abandoned type, but still leaving for old order lookup
        return "My Hotel Movies";
      case OrderType.StreamingServer:
        return "Streaming Server";
      case OrderType.All:
        return "All Order Types";
      default:
        return "Unknown";
    }
  }

  public mapOrderMediaType(mediaType: MediaType): string {
    switch (mediaType) {
      case MediaType.Cvd:
        return "CVD";
      case MediaType.Electronic:
        return "Electronic";
      case MediaType.HardDrive:
        return "Hard Drive";
      case MediaType.Unknown:
        return "Unknown";
      case MediaType.All:
        return "All Media Types";
      case MediaType.NetworkDelivery:
        return "Electronic";
      default:
        return "Unknown";
    }
  }

  public mapOrderErrorType(errorType: ErrorType | number): string {
    switch (errorType) {
      case ErrorType.FileSizeError:
        return "File Size Error";
      case ErrorType.Hd3Error:
        return "HD3 Error";
      case ErrorType.MissingContent:
        return "Missing Content";
      case ErrorType.MissingPublicity:
        return "Missing Publicity";
      case ErrorType.MissingTrailer:
        return "Missing Trailer";
      case ErrorType.PastShipDate:
        return "Past Ship Date";
      case ErrorType.ScheduleError:
        return "Schedule Error";
      case ErrorType.ThresholdViolation:
        return "Threshold Violation";
      case ErrorType.All:
        return "All Error Types";
      case ErrorType.Unknown:
        return "Other"; // It is called "Other" on the server side. Probably later need to change enum
      default:
        return "Unknown";
    }
  }

  public mapDrmType(drmType: DrmType | number): string {
    switch (drmType) {
      case DrmType.All:
        return "all";
      case DrmType.AES:
        return "aes";
      case DrmType.Cenc:
        return "cenc";
      case DrmType.Cenc2:
        return "cenc2";
      case DrmType.Cmaf:
        return "cmaf";
      case DrmType.Playready:
        return "playready";
      case DrmType.Proidiom:
        return "proidiom";
      case DrmType.SamsungLynk:
        return "samsung lynk";
      case DrmType.SecretAgent:
        return "secret agent"
      case DrmType.Widevine:
        return "widevine";
      default:
        return "unknown";
    }
  }

  public mapOrderDetailsStatus(lineStatus: LineStatus, orderType: OrderType): string {
    // TODO: Need to talk with fulfillment team about renaming this for ADEBS
    // not sure if we want to actually call this complete...or pending ship across the board
    if (orderType === OrderType.Adebs && lineStatus === LineStatus.Complete) {
      return "Pending Ship";
    }

    return this.mapOrderShipmentStatus(lineStatus);
  }

  public calculatePastShipDate(shipment: OrderShipment): ErrorMessage | undefined {
    const shipDate = new Date(shipment.shipDate);
    const now = new Date();
    const formattedCurrentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

    if (shipDate.getTime() < formattedCurrentDate && shipment.status !== LineStatus.Complete) {
      const message = `Shipment is past ship date (${shipDate.getMonth() + 1}/${shipDate.getDate()}/${shipDate.getFullYear()})`;
      const errorMessage = new ErrorMessage(message, new Date(), ErrorType.PastShipDate, "PastShipDate");
      if (shipment.errors.some((e) => e.type === ErrorType.PastShipDate)) {
        return undefined;
      } else {
        return errorMessage;
      }
    }
    return undefined;
  }

  public applyFilters(
    orderShipmentGroups: OrderShipmentGroupsList,
    filter: OrderFilter,
    searchText: string
  ): OrderShipmentGroup[] {
    // this function takes all supplied orders, and filters out everything with the filter types and freeform text.
    // returns the remaining orders that match all the filter criteria
    if (orderShipmentGroups === undefined || orderShipmentGroups.orderShipmentGroups.length == 0) {
      return [];
    }

    let filteredGroups: OrderShipmentGroup[] = orderShipmentGroups.orderShipmentGroups.map((group) => {
      const groupCopy = new OrderShipmentGroup();
      groupCopy.status = group.status;
      groupCopy.orderShipments = group.orderShipments;
      return groupCopy;
    });

    // if status filter is selected that isn't all, filter out all orders by status
    if (filter.statusFilter && filter.statusFilter !== LineStatus.All) {
      filteredGroups = filteredGroups.filter((g) => g.status === filter.statusFilter);
    }

    filteredGroups.forEach((g) => {
      const filteredShipments: OrderShipment[] = [];
      for (const shipment of g.orderShipments) {
        // should we only show warnings?
        if (filter.warningsErrorsFilter) {
          if (shipment.errors.length <= 0) {
            continue;
          }
        }

        // should we filter out cancelled orders
        if (filter.cancelledOrdersFilter) {
          if (shipment.allLinesAreCancelled) {
            continue;
          }
        }

        // filter by freeform text field
        if (searchText !== "") {
          const formattedSearch = searchText.toLocaleUpperCase();
          const searchEvaluation =
            `${shipment.orderNumber}`.toLocaleUpperCase().includes(formattedSearch) ||
            shipment.customerName.toLocaleUpperCase().includes(formattedSearch) ||
            (shipment.portalId && shipment.portalId.toLocaleUpperCase().includes(formattedSearch));

          if (!searchEvaluation) {
            continue;
          }
        }

        // filter by error types as long as we have one selected
        if (filter.errorFilter && !filter.errorFilter.includes(ErrorType.All)) {
          if (!shipment.errors.some((e) => filter.errorFilter.includes(e.type))) {
            continue;
          }
        }

        // filter out ship date fields
        const shipDate = new Date(shipment.shipDate);
        if (filter.fromShipDateFilter) {
          const fromDate = filter.fromShipDateFilter as Date;
          fromDate.setHours(0, 0, 0, 0); // beginning of day for min
          if (shipDate.getTime() < fromDate.getTime()) {
            continue;
          }
        }

        if (filter.toShipDateFilter) {
          const toDate = filter.toShipDateFilter as Date;
          toDate.setHours(23, 59, 59); // look through end of the day for max
          if (shipDate.getTime() > toDate.getTime()) {
            continue;
          }
        }

        // filter order type like ADEBS, streaming, or cinelink
        if (filter.orderTypeFilter && !filter.orderTypeFilter.includes(OrderType.All)) {
          if (!filter.orderTypeFilter.includes(shipment.orderType)) {
            continue;
          }
        }

        // filter out media type: Electronic, CVD, Hard drive
        if (filter.mediaFilter && !filter.mediaFilter.includes(MediaType.All)) {
          if (!filter.mediaFilter.includes(shipment.mediaType)) {
            continue;
          }
        }

        // filter out orders that are not currently in status: Burning, watermark, Pending, Licensing, packaging, etc.
        if (filter.statusFilter && filter.statusFilter !== LineStatus.All) {
          if (shipment.status !== filter.statusFilter) {
            continue;
          }
        }

        // filter out results by market
        if (filter.marketFilter) {
          let allMarketSelected = false;
          filter.marketFilter.forEach((mrkt: Market) => {
            if (mrkt.orgType === "all") {
              allMarketSelected = true;
            }
          });

          let shipmentHasMarket = false;
          filter.marketFilter.forEach((market: Market) => {
            if (market.orgType === shipment.marketOrgType && market.salesDivision === shipment.marketSalesDivision) {
              shipmentHasMarket = true;
            }
          });

          if (allMarketSelected === false && shipmentHasMarket === false) {
            continue;
          }
        }

        // if filtering by DRM type
        // NOTE: this value comes in as a string.
        // we seem to add new DRM types. We might want to eventually get a list of DRM types
        // from the database as expected values. This is a bit hacky now with translating strings to drm types
        if (filter.drmFilter) {
          /// The DrmType enum 0 is "all", so we don't do the additional all check like some of the other filters
          if (!shipment.drmType) {
            continue;
          } // shipment does not have DRM and we are filter by one...just ignore this entry

          const drmFilterAsString = this.mapDrmType(filter.drmFilter);
          let doDrmTypesMatch = shipment.drmType.toLowerCase().trim().includes(drmFilterAsString);

          // if our filter is set to CENC, we want to filter out CENC2 orders since the text match overlaps
          if (filter.drmFilter === DrmType.Cenc) {
            doDrmTypesMatch = shipment.drmType.toLowerCase().trim().includes("cenc2") ? false : doDrmTypesMatch;
          }

          if (doDrmTypesMatch === false) {
            continue;
          }
        }

        // filter out results by sales rep
        if (filter.salesRepFilter && filter.salesRepFilter !== "all") {
          if (shipment.accountExecutive.trim().toLowerCase() !== filter.salesRepFilter.trim().toLowerCase()) {
            continue;
          }
        }

        // filter by order source system (Dynamics vs Arc/Contracts)
        if (filter.orderSourceSystemFilter != null && filter.orderSourceSystemFilter !== OrderSourceSystem.All) {
          const shipmentIsDynamics = shipment.orderSourceSystem === OrderSourceSystem.Dynamics;
          const filterWantsDynamics = filter.orderSourceSystemFilter === OrderSourceSystem.Dynamics;
          if (shipmentIsDynamics !== filterWantsDynamics) {
            continue;
          }
        }

        filteredShipments.push(shipment);
      }

      g.orderShipments = filteredShipments;
    });

    // no use keeping shipment groups that have no shipments, so filter those out before we return the final result
    filteredGroups = filteredGroups.filter((group) => group.orderShipments.length > 0);

    return orderBy(filteredGroups, "status", "asc");
  }

  public dateCompareToToday(dateStringVal: string): number {
    const dateToConvert = new Date(dateStringVal);
    dateToConvert.setHours(0, 0, 0, 0); // hopefully helps reduce time zone issues
    const today = new Date();
    today.setHours(0, 0, 0);
    const timeDifference = dateToConvert.getTime() - today.getTime();
    const differenceInDays = timeDifference / (1000 * 3600 * 24); // (milliseconds * seconds in hour * hours in day)
    return Math.round(differenceInDays);
  }

  public getMarketName(marketOrgType: string | null, showCode: boolean = true): string {

    if (!marketOrgType) {
      return "Not specified";
    }

    const code = marketOrgType.trim().toUpperCase();
    let response = OrderUtilityService.MARKET_NAMES[code] ?? `!${code}!`;

    if (showCode) {
      response += ` (${code})`;
    }

    return response;
  }

  public convertDateStringsToDatesForJSON(originalJson: any): any {
    // JSON data comes back from the server as strings...even dates. The typescript data model expects dates
    // to be a date object. This convert dates to date objects before returning

    for (const index in originalJson) {
      for (const prop in originalJson[index]) {
        // update property if it is in date format
        if (/^\d\d\d\d-\d\d-\d\d/.test(originalJson[index][prop])) {
          originalJson[index][prop] = new Date(originalJson[index][prop]);
        }
      }
    }

    return originalJson;
  }

  public isOrderShipmentComplete(ordershipment: OrderShipment): boolean {
    // all these various statuses are considered done/complete
    return ordershipment.lines.every((l) =>
      [LineStatus.Complete, LineStatus.Cancelled, LineStatus.Shipped].includes(l.status)
    );
  }

  /*
    Tells us if all the order lines have the same license start and end date
    If all the order lines have the same license start and end date, no need to show the content on every order line
  */
  public isAllLicensesSameDateRange(orderLines: OrderLine[]): boolean {

    // grab the first line's license start date and end date. Get all the lines that match this date range
    // dates come in as strings, so a simple string compare is sufficient
    return orderLines.every(l => l.validFrom === orderLines[0].validFrom && l.validTo === orderLines[0].validTo);
  }

  public getOrderLegendItems(): LegendItem[] {
    return [
      new LegendItem("ADEBS - CVD", "#ffa30c", true),
      new LegendItem("ADEBS - HDD", "#ffd10c", true),
      new LegendItem("ADEBS - Electronic", "#ffec75", true),
      new LegendItem("Streaming - Electronic", "#0962a6", true),
      new LegendItem("Streaming - HDD", "#00d7ff", true),
      new LegendItem("Cinelink - Electronic", "#00ae00", true),
      new LegendItem("Cinelink - HDD", "#42ff3f", true),
    ];
  }

  public getErrorAbbreviation(errorType: string): string {
    const errorCode = parseInt(errorType, 10);
    switch (errorCode) {
      case ErrorType.FileSizeError:
        return 'FTL';
      case ErrorType.Hd3Error:
        return 'HD3';
      case ErrorType.MissingContent:
        return 'MC';
      case ErrorType.MissingPublicity:
        return 'MP';
      case ErrorType.MissingTrailer:
        return 'MT';
      case ErrorType.PastShipDate:
        return 'PS';
      case ErrorType.ScheduleError:
        return 'MS';
      case ErrorType.ThresholdViolation:
        return 'THR';
      case ErrorType.Unknown:
        return 'OT';
      case ErrorType.All:
        return 'ALL'; // only occurs in the filters component
      default:
        return 'OT';
    }
  }

  public isAnyCompletedOrder(orderShipment: OrderShipment): boolean {
    return this.isCompletedAdebsOrder(orderShipment) || this.isCompletedStreamingOrder(orderShipment) || this.isCompletedCinelinkOrder(orderShipment);
  }

  public isCompletedAdebsOrder(orderShipment: OrderShipment): boolean {
    const validMediaTypes = [MediaType.HardDrive, MediaType.Cvd, MediaType.NetworkDelivery];
    return this.isCompletedOrder(orderShipment) && orderShipment.orderType === OrderType.Adebs && validMediaTypes.includes(orderShipment.mediaType);
  }

  public isCompletedStreamingOrder(orderShipment: OrderShipment): boolean {
    return this.isCompletedOrder(orderShipment) && orderShipment.orderType === OrderType.StreamingServer;
  }

  public isCompletedCinelinkOrder(orderShipment: OrderShipment): boolean {
    return this.isCompletedOrder(orderShipment) && orderShipment.orderType === OrderType.Cinelink;
  }

  public isCompletedOrder(orderShipment: OrderShipment): boolean {
    return orderShipment.lines.every(line =>
      [LineStatus.Complete, LineStatus.Cancelled, LineStatus.Shipped].includes(line.status)
    );
  }

  public isShipmentCancelled(orderShipment: OrderShipment): boolean {
    return orderShipment.shipmentStatus === 'CN';
  }
}
